import { DestroyRef, Injectable, Injector, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { Firestore, Unsubscribe } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { FirestoreApi } from '@core/services/data/firestore-api.service';
import { AuthStore } from '@core/services/auth/auth.store';
import {
  NotificationCategory,
  type Notification,
  type NotificationSummary,
  type NotificationFilter,
  type NotificationGrouping,
  type AddNotificationRequest,
  type GetNotificationsRequest,
  type GetNotificationsResponse,
  type NotificationResponse
} from '../models/notification.model';
import type { INotificationsStore } from './notifications-store.interface';

/**
 * Firestore implementation of the user-scoped notifications store.
 *
 * Owns the real-time `onSnapshot` subscription on
 * `users/{userId}/notifications/data` plus all Firebase Functions callables
 * (`addNotification`, `markNotificationAsRead`, `markAllNotificationsAsRead`,
 * `removeNotification`, `getNotifications`, `cleanupExpiredNotifications`,
 * `createTestNotifications`). Replaces the legacy `NotificationService` retired
 * as part of Phase C (2026-04-20).
 *
 * Note: there is a separate `NotificationService` at
 * `src/app/core/services/shared/utilities/notification.service.ts` for toast /
 * loading UX — the two are unrelated.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsFirestoreStore implements INotificationsStore, OnDestroy {
  private readonly firestore = inject(Firestore);
  private readonly fs = inject(FirestoreApi);
  private readonly functions = inject(Functions);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private readonly _notifications = signal<readonly Notification[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly notifications = this._notifications.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._notifications().length);
  readonly unread = computed(() => this._notifications().filter(n => !n.read));
  // Urgentes ET non lues : ce flux pilote le rouge de la cloche, qui doit
  // s'éteindre dès que tout est lu (#641).
  readonly urgent = computed(() => this._notifications().filter(n => n.priority === 'urgent' && !n.read));
  readonly unreadCount = computed(() => this.unread().length);
  readonly urgentCount = computed(() => this.urgent().length);

  /**
   * Summary is derived from the live notifications signal so it stays in sync
   * automatically — no need for a separate BehaviorSubject like the legacy
   * service used.
   */
  readonly summary = computed<NotificationSummary | null>(() => {
    const list = this._notifications();
    return {
      totalCount: list.length,
      unreadCount: list.filter(n => !n.read).length,
      categories: {
        [NotificationCategory.FINANCIAL]: list.filter(n => n.category === NotificationCategory.FINANCIAL).length,
        [NotificationCategory.CONTRACTS]: list.filter(n => n.category === NotificationCategory.CONTRACTS).length,
        [NotificationCategory.MAINTENANCE]: list.filter(n => n.category === NotificationCategory.MAINTENANCE).length,
        [NotificationCategory.DOCUMENTS]: list.filter(n => n.category === NotificationCategory.DOCUMENTS).length,
        [NotificationCategory.SYSTEM]: list.filter(n => n.category === NotificationCategory.SYSTEM).length,
        [NotificationCategory.REMINDERS]: list.filter(n => n.category === NotificationCategory.REMINDERS).length,
        [NotificationCategory.PROVIDERS]: list.filter(n => n.category === NotificationCategory.PROVIDERS).length
      }
    };
  });

  private unsubscribeSnapshot: Unsubscribe | null = null;

  constructor() {
    // Defer the auth-driven listener setup off the bootstrap critical path.
    // The notifications bell reads signals via toObservable / computed so a
    // microtask delay is invisible to consumers but frees up first paint.
    queueMicrotask(() => {
      effect(() => {
        const isAuthenticated = this.authStore.isAuthenticated();
        if (isAuthenticated) {
          const user = this.authStore.user();
          if (user?.userUID) {
            this.setupNotificationsListener(user.userUID);
          }
        } else {
          this.clearNotificationData();
        }
      }, { manualCleanup: false, injector: this.injector });
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeSnapshot?.();
    this.unsubscribeSnapshot = null;
  }

  async refresh(): Promise<void> {
    try {
      await this.callFunction<GetNotificationsResponse>('getNotifications', {});
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
    }
  }

  async addNotification(request: AddNotificationRequest): Promise<NotificationResponse> {
    return this.callFunction<NotificationResponse>('addNotification', request);
  }

  async markAsRead(notificationId: string): Promise<NotificationResponse> {
    const result = await this.callFunction<NotificationResponse>('markNotificationAsRead', { notificationId });
    // Optimistic update — flip the flag locally so the bell badge reacts
    // instantly, without waiting for the onSnapshot listener to emit.
    this._notifications.set(
      this._notifications().map(n =>
        n.notificationId === notificationId ? { ...n, read: true, readAt: new Date() } : n
      )
    );
    return result;
  }

  async markAllAsRead(): Promise<NotificationResponse> {
    const result = await this.callFunction<NotificationResponse>('markAllNotificationsAsRead', {});
    const now = new Date();
    this._notifications.set(
      this._notifications().map(n =>
        n.read ? n : { ...n, read: true, readAt: now }
      )
    );
    return result;
  }

  async remove(notificationId: string): Promise<NotificationResponse> {
    const result = await this.callFunction<NotificationResponse>('removeNotification', { notificationId });
    this._notifications.set(this._notifications().filter(n => n.notificationId !== notificationId));
    return result;
  }

  async fetch(request: GetNotificationsRequest = {}): Promise<GetNotificationsResponse> {
    return this.callFunction<GetNotificationsResponse>('getNotifications', request);
  }

  async cleanupExpired(): Promise<NotificationResponse> {
    return this.callFunction<NotificationResponse>('cleanupExpiredNotifications', {});
  }

  async createTestNotifications(count = 1): Promise<NotificationResponse> {
    return this.callFunction<NotificationResponse>('createTestNotifications', { count });
  }

  filter(filter: NotificationFilter): readonly Notification[] {
    return this._notifications().filter(n => {
      if (filter.unreadOnly && n.read) return false;
      if (filter.priority && n.priority !== filter.priority) return false;
      if (filter.category && n.category !== filter.category) return false;
      if (filter.search) {
        const q = filter.search.toLowerCase();
        const hay = `${n.title} ${n.message}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }

  grouped(): NotificationGrouping {
    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfThisWeek = new Date(startOfToday);
    startOfThisWeek.setDate(startOfThisWeek.getDate() - 7);

    const groups: NotificationGrouping = { today: [], yesterday: [], thisWeek: [], older: [] };
    for (const n of this._notifications()) {
      const d = new Date(n.createdAt);
      if (d >= startOfToday) groups.today.push(n);
      else if (d >= startOfYesterday) groups.yesterday.push(n);
      else if (d >= startOfThisWeek) groups.thisWeek.push(n);
      else groups.older.push(n);
    }
    return groups;
  }

  // --- internals ---

  private setupNotificationsListener(userId: string): void {
    this.unsubscribeSnapshot?.();
    const notificationsRef = this.fs.doc(this.firestore, `users/${userId}/notifications/data`);
    this.unsubscribeSnapshot = this.fs.onSnapshot(
      notificationsRef,
      docSnapshot => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const items = (data?.['items'] as Record<string, unknown>[] | undefined) ?? [];
          const toJsDate = (v: unknown): Date | undefined => {
            if (!v) return undefined;
            if (v instanceof Date) return v;
            if (typeof v === 'object' && v && 'toDate' in v && typeof (v as { toDate?: unknown }).toDate === 'function') {
              return (v as { toDate: () => Date }).toDate();
            }
            return new Date(v as string | number);
          };
          const notifications = items.map(item => ({
            ...item,
            createdAt: toJsDate(item['createdAt']) ?? new Date(),
            readAt: toJsDate(item['readAt']),
            expiresAt: toJsDate(item['expiresAt'])
          })) as unknown as Notification[];
          this._notifications.set(notifications);
        } else {
          this._notifications.set([]);
        }
      },
      err => {
        // Keep existing notifications on transient listener error — don't clear.
        this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      }
    );
  }

  private clearNotificationData(): void {
    this.unsubscribeSnapshot?.();
    this.unsubscribeSnapshot = null;
    this._notifications.set([]);
  }

  private async callFunction<T>(name: string, data: unknown): Promise<T> {
    try {
      const callable = this.fs.httpsCallable(this.functions, name);
      const result = await callable(data);
      return result.data as T;
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  // destroyRef reserved for future manualCleanup of the effect if ever needed
  private readonly _destroyRefHold = this.destroyRef;
}
